/** Shapes returned by the Django API. Mirrors the DRF serializers 1:1. */

export type Role = 'owner' | 'admin' | 'manager' | 'viewer'

/** clients_reservations.client_paid */
export const PaymentType = {
  Unpaid: 0,
  Cash: 1,
  Card: 2,
  Invoice: 3,
  /** Prevodom na účet - aj QR platba a import z banky. */
  Transfer: 4,
  /** Z kreditu zákazníka (vrátená platba pripísaná na ďalšiu rezerváciu). */
  Credit: 5,
  Other: 99,
} as const
export type PaymentType = (typeof PaymentType)[keyof typeof PaymentType]

/** reserve_status.id - PRE / DEF / ZRU */
export const ReserveStatus = {
  Preliminary: 1,
  Confirmed: 2,
  Cancelled: 3,
} as const
export type ReserveStatusId = (typeof ReserveStatus)[keyof typeof ReserveStatus]

export interface User {
  id: number
  email: string
  firstname: string | null
  lastname: string | null
  full_name: string
  phone_prefix: string | null
  phone: string | null
  role: Role
  block: boolean
  created: string
  can_write: boolean
  can_administrate: boolean
  /** Dvojfaktorové overenie (kód z aplikácie) je zapnuté. */
  mfa_enabled?: boolean
}

export interface Codebook {
  id: number
  key: string | null
  name: string | null
  color?: string | null
  use?: boolean
}

export interface Settings {
  id: number
  system_id: number | null
  company_name: string | null
  system_name: string | null
  logo: string | null
  logo_url: string
  /** Nahraté logo bez ohľadu na balíček (náhľad v Nastaveniach). */
  own_logo: string
  /** Kontakt školy v pätičke e-mailov a Reply-To (od balíčka s vlastným brandom). */
  contact_email: string
  /** Pripomienka neuhradenej objednávky X dní pred prvou lekciou (0 = vypnuté). */
  payment_reminder_days: number
  /** Ikona do záložky, vyrobená z loga. Prázdne = predvolená z buildu. */
  favicon_url: string
  start_hour: number
  end_hour: number
  parts_in_hour: number
  max_clients_per_reservation: number
  lang: string
  currency_id: number
  currency_key: string
  email_notifications: boolean
  sms_notifications: boolean
  messenger_notifications: boolean
  module_pricing: boolean
  module_cash_receipt: boolean
  /** Platby eviduje škola inde - v rezervácii len označí úhradu (`sql/028`). */
  simple_payments: boolean
  /** Online platba kartou za rezerváciu z widgetu. */
  module_stripe: boolean
  /**
   * Kľúče Stripe. Z API sa **nikdy nevracajú** — dajú sa len zapísať, takže
   * pri načítaní sú `undefined`. Že nastavené sú, povie
   * `modules.stripe.configured`.
   */
  stripe_secret_key?: string
  stripe_webhook_secret?: string
  /** Import platieb z Fio Banka. */
  module_fio: boolean
  /** Token Fio Banka. Rovnaké pravidlo ako pri Stripe kľúčoch vyššie -
   *  z API sa nikdy nevracia. */
  fio_token?: string
  /** Kedy naposledy prebehol import - len na zobrazenie, server ho sám píše. */
  fio_last_import_at: string | null
  /** Banky, z ktorých sa importujú výpisy - kódy oddelené čiarkou
   *  (`tatra,slsp,…`, viď `features/settings/banks.tsx`). */
  statement_banks: string
  /** `{kód banky: čas posledného importu}`. */
  statement_imports: Record<string, string> | null
  /** QR platba (Pay by Square) za rezerváciu, na účet školy. */
  module_qr_payment: boolean
  /** IBAN účtu školy. Na rozdiel od Stripe kľúčov / Fio tokenu to nie je
   *  tajomstvo - škola ho aj tak dáva zákazníkom, takže sa z API vracia. */
  qr_iban: string
  /** Receipt numbering: prefix + optional year + zero-padded sequence. */
  receipt_prefix: string
  receipt_include_year: boolean
  receipt_number_length: number
  /** Prefix dokladov o vrátenej platbe (VP) - rok a dĺžka ako pri potvrdeniach. */
  refund_prefix: string
  /** Faktúry klientom - prefix radu (FA), sadzba DPH (null = podľa krajiny
   *  školy), splatnosť v dňoch a text na každú faktúru. */
  invoice_prefix: string
  invoice_vat_rate: string | null
  invoice_due_days: number
  invoice_note: string
  /** Prefix radu storien faktúr (opravná faktúra, OFA). */
  invoice_storno_prefix: string
  /** Prefix radu výplatných listov inštruktorov (VL). */
  payslip_prefix: string
  interval: number
  parts: number
  modules: {
    pricing: { enabled: boolean }
    cash_receipt: { enabled: boolean }
    /** `enabled` je prepínač, `configured` znamená „a má aj kľúč“. */
    stripe: { enabled: boolean; configured: boolean }
    fio: { enabled: boolean; configured: boolean }
    statement: { banks: string[] }
    qr_payment: { enabled: boolean; configured: boolean }
  }
  display_company_name: string
  display_system_name: string
}

/** Jedna transakcia z Fio Banka - viď `apps.reservations.fio_gateway`. */
export interface FioTransaction {
  date: string | null
  amount: string | number | null
  currency: string | null
  variable_symbol: string | null
  counterparty: string
  message: string
  transaction_id: string | null
}

/** Odpoveď na `POST /settings/integrations/fio/import`. */
export interface FioImportResult {
  from: string
  to: string
  matched: Array<FioTransaction & { ref: number; payment_id: number }>
  unmatched: Array<FioTransaction & { reason: 'no_match' | 'no_due' }>
  matched_count: number
  unmatched_count: number
  skipped_count: number
}

/** Odpoveď na `POST /settings/integrations/tatra/import` - to isté ako pri
 *  Fio, plus účet a obdobie z nahraného výpisu. */
export interface StatementImportResult extends FioImportResult {
  bank: string
  /** Názov banky, ktorej účet je vo výpise, keď to nie je zvolená banka. */
  other_bank: string
  account: string
  /** Výpis je z iného účtu, než je IBAN školy v nastaveniach QR platby. */
  other_account: boolean
}

export interface Bootstrap {
  settings: Settings
  courses: Codebook[]
  languages: Codebook[]
  levels: Codebook[]
  statuses: Codebook[]
  currencies: Codebook[]
  /** Aktívne vlastné bonusy inštruktorov (`sql/038`). */
  reward_bonuses?: RewardBonus[]
  /** Strediská školy (`sql/040`); výbery až pri `multi_center`. */
  centers?: CenterSummary[]
  multi_center?: boolean
  /** Testovacie prostredie pred migráciou - klientom sa nič neposiela. */
  sandbox?: { enabled: boolean; email: string }
  /** Balíček školy a čo obsahuje (`apps.tenants.features`). */
  plan?: {
    key: string
    name: string
    features: Record<PlanFeature, boolean>
    /** Názov najnižšieho balíčka, ktorý funkciu má. */
    feature_from: Record<PlanFeature, string>
  }
}

export type PlanFeature =
  | 'qr_payment'
  | 'bank_import'
  | 'card_payments'
  | 'widget'
  | 'statistics_pro'
  | 'accounting_export'
  | 'email_brand'
  | 'client_reminders'
  | 'messenger'
  | 'email_notifications'
  | 'ai_chat'
  | 'multi_center'

export interface Client {
  id: number
  created: string
  fname: string | null
  sname: string | null
  full_name: string
  email: string | null
  tel: string | null
  note: string | null
  levelid: number | null
  lang_id: number | null
  course_id: number | null
  is_children: boolean
  is_beginner: boolean
  user_id: number | null
  reservations_count?: number
  last_reservation?: string | null
  total_spent?: string | null
}

/** Skupina klientov, ktorí sú podľa kontaktu tá istá osoba. */
export interface DuplicateGroup {
  key: string
  value: string
  /** `tel:905123456`, `email:jan@x.sk` — dôvody, prečo skupina drží spolu. */
  reasons: string[]
  clients: Client[]
}

export interface MergeResult {
  client: Client
  /** Počty presunutých riadkov podľa tabuľky, vrátane `clients_deleted`. */
  moved: Record<string, number>
  moved_reservations: number
}

/**
 * Prepojenie inštruktora s Messengerom.
 *
 * Vydáva sa až pri otvorení detailu, takže v zozname je `null` — token pre
 * inštruktora, ktorého nikto neotvoril, nemá prečo existovať.
 */
export interface MessengerLink {
  /** Bot je pre platformu nastavený. Inak je toto jediné pole. */
  enabled: boolean
  /** Kód na napísanie botovi, keď odkaz nefunguje. */
  token?: string
  /** `m.me/<stránka>?ref=<token>` — inštruktor klikne a nepíše nič. */
  url?: string
  linked?: boolean
  linked_at?: string | null
}

export interface Instructor {
  id: number
  hash: string | null
  /** Prepojenie s Messengerom. `null` mimo detailu inštruktora. */
  messenger: MessengerLink | null
  created: string
  fname: string | null
  sname: string | null
  full_name: string
  email: string | null
  tel: string | null
  note: string | null
  idslang: string
  idscourses: string
  language_ids: number[]
  course_ids: number[]
  messenger_psid: string | null
  deleted: boolean
  favorite: boolean
  priority: number
  reservations_count?: number
  last_reservation?: string | null
  rewards_total?: string | null
}

export interface InstructorReservation {
  id: number
  created: string | null
  instructor: Pick<Instructor, 'id' | 'fname' | 'sname' | 'full_name' | 'email' | 'tel' | 'favorite' | 'priority'> | null
  instructor_id: number | null
  start: string | null
  end: string | null
  status_id: number | null
  idslang: string | null
  idscourses: string | null
  language_ids: number[]
  course_ids: number[]
  owncar: boolean
  passengers_in_car: number
  note: string | null
  duration_hours: number
  /** Stredisko bloku (`sql/040`). */
  center_id?: number
}

/** Stredisko v `bootstrap` - na výbery a filtre. */
export interface CenterSummary {
  id: number
  name: string
  color: string
  active: boolean
  /** Aktívne účty strediska, predvolený prvý (`sql/042`). */
  accounts?: Array<{ id: number; name: string; iban: string; is_default: boolean }>
}

/** Účet (IBAN) strediska - Nastavenia (`/api/bank-accounts`). */
export interface BankAccount {
  id: number
  center_id: number
  name: string
  iban: string
  is_default: boolean
  active: boolean
  position: number
}

/** Stredisko v Nastaveniach (`/api/centers`). */
export interface Center extends CenterSummary {
  position: number
  own_identity: boolean
  legal_name: string
  street: string
  city: string
  postal_code: string
  country_code: string
  ico: string
  dic: string
  vat_id: string
  registration: string
  email: string
  phone: string
  iban: string
  receipt_prefix: string | null
  refund_prefix: string | null
  invoice_prefix: string | null
  invoice_storno_prefix: string | null
}

export interface ClientReservation {
  id: number
  created: string
  client: Pick<
    Client,
    | 'id' | 'fname' | 'sname' | 'full_name' | 'email' | 'tel'
    | 'is_children' | 'is_beginner' | 'levelid' | 'note'
  > | null
  client_id: number | null
  instructor_reservation_id: number | null
  instructor_id: number | null
  instructor_name: string | null
  start: string | null
  end: string | null
  status_id: number | null
  lang_id: number
  course_id: number
  numbers: number
  client_payment: number | null
  client_paid: PaymentType
  note: string | null
  /** Variabilný symbol — spoločný pre lekcie objednané naraz. */
  payment_ref: number | null
  /** Kurz objednaný cez školu, nie bežná rezervácia klienta. */
  is_school_course: boolean
  duration_minutes: number
  fees_total: string | number | null
  /** Lekcia je na vydanom doklade — presun aj vrátenie zmeny sa nedá
   *  dokončiť bez storna toho dokladu. */
  has_issued_receipt: boolean
  /** Koľko na objednávku prišlo (po vráteniach) - pre „čiastočne uhradené“. */
  order_received?: string
  /** Zaznačil inštruktor v mobilnom pohľade: 1 odučená, 2 neprišiel. */
  attendance?: Attendance | null
  attendance_at?: string | null
  /** Zaškrtnuté vlastné bonusy inštruktora (`sql/038`). */
  bonus_ids?: number[]
  /** Stredisko lekcie (`sql/040`). */
  center_id?: number
  /** Účet, na ktorý má klient platiť; null = predvolený účet strediska. */
  bank_account_id?: number | null
}

/**
 * Riadok zoznamu rezervácií — to isté plus to, čo sa dá dopočítať.
 *
 * Kalendár tieto polia nedostáva: sú to poddotazy a on ťahá stovky riadkov
 * naraz, hoci ich nezobrazuje.
 */
export interface ClientReservationRow extends ClientReservation {
  /** Rezervácia prišla cez widget, nie z recepcie. */
  is_online: boolean
  /** Koľko lekcií nesie ten istý variabilný symbol. */
  order_size: number
}

/** Ktorá auditná tabuľka - jedna záložka prehľadu zmien. */
export type ChangeKind =
  | 'client-reservations'
  | 'clients'
  | 'instructors'
  | 'instructor-reservations'

/** Jedna zmena v prehľade zmien - rozdiel oproti predchádzajúcemu stavu. */
export interface ChangeEntry {
  id: number
  created: string | null
  type: 'insert' | 'update' | 'delete' | null
  /** Id predmetu zmeny: lekcie, klienta, inštruktora alebo bloku. */
  subject_id: number | null
  /** Meno, ktorého sa zmena týka. */
  subject: string | null
  /** Čas lekcie/bloku a inštruktor - podľa záložky môže byť prázdne. */
  context: { start: string | null; end?: string | null; instructor: string | null }
  user_id: number | null
  user_name: string | null
  /** `field: '*'` znamená vytvorenie - nemá staré hodnoty. */
  changes: Array<{ field: string; old: unknown; new: unknown }>
  /** Vrátiť sa dá zatiaľ len lekcia klienta, a nie jej vytvorenie. */
  revertable: boolean
}

export interface CalendarSlot {
  index: number
  label: string
  hour: number
}

export interface CalendarGrid {
  start: string
  end: string
  days: number
  start_hour: number
  end_hour: number
  parts_in_hour: number
  interval: number
  parts: number
  slots: CalendarSlot[]
  dates: string[]
  max_clients_per_reservation: number
}

export interface CalendarResponse {
  grid: CalendarGrid
  instructor_reservations: InstructorReservation[]
  client_reservations: ClientReservation[]
  instructors: Array<Pick<Instructor, 'id' | 'fname' | 'sname' | 'full_name' | 'favorite' | 'priority' | 'idslang' | 'idscourses'>>
  courses: Codebook[]
  languages: Codebook[]
  levels: Codebook[]
  statuses: Codebook[]
  is_viewer: boolean
}

export type DiscountType = 'percent' | 'amount'

export interface ClientFee {
  id: number
  updated: string
  type_id: number | null
  type_name: string | null
  unique_id: string | null
  description: string | null
  client_id: number | null
  client_name: string | null
  client_reservation_id: number | null
  reservation_start: string | null
  /** What is actually charged - all totals sum this. */
  amount: string
  /** Price before the discount; null when no discount is applied. */
  gross_amount: string | null
  discount_type: DiscountType | null
  discount_value: string | null
  discount_amount: string | null
  paid: boolean
  cash_receipt_id: number | null
  cash_receipt_number: string | null
  is_custom: boolean
  is_basic: boolean
  is_total_discount: boolean
}

/** Jedna prijatá platba, viazaná na variabilný symbol. */
export interface Payment {
  id: number
  created: string
  ref: number
  amount: string
  payment_type: PaymentType
  note: string
  cash_receipt_id: number | null
  /** Platba z vydaného dokladu — zmazať ju vie len storno dokladu. */
  from_receipt: boolean
  /** Doklad k platbe - `receipt` (potvrdenie), `invoice` (faktúra, ktorú
   *  Stripe vystavoval pri starších platbách).
   *  Platba s dokladom sa nemaže, peniaze sa vracajú vrátením alebo kreditom. */
  document?: 'receipt' | 'invoice' | null
  /** UID / číslo dokladu z eKasy školy (platba v hotovosti). */
  ekasa_ref?: string
  /** Poplatok Stripe a čistá suma na účet (null - ešte nie je známy). */
  fee?: string | null
  net?: string | null
  fee_currency?: string
}

/** Odkiaľ platba pochádza — stĺpec „Pôvod“ v zozname platieb. */
/** `manual`, `receipt`, `fio` alebo kód banky z výpisu (`tatra`, `slsp`…). */
export type PaymentSource = string

/** Riadok v `GET /api/payments` — platba doplnená o objednávku, ktorej patrí. */
export interface PaymentListRow extends Payment {
  client_id: number | null
  client_name: string | null
  lesson_start: string | null
  source: PaymentSource
}

/** Prečo sa platba z importu nespárovala automaticky. */
export type UnmatchedReason = 'no_match' | 'no_due'

/** Riadok v `GET /api/payments/unmatched` — front na ručné spárovanie. */
export interface UnmatchedPaymentRow {
  id: number
  created: string
  transaction_date: string
  amount: string
  variable_symbol: string
  counterparty: string
  message: string
  reason: UnmatchedReason
  reason_label: string
}

/**
 * Objednávka — všetko, čo visí na jednom variabilnom symbole.
 *
 * `total` je predpis (súčet položiek všetkých lekcií), `paid` súčet platieb
 * a `due` zostatok. `settled` nie je `due === 0`: objednávka bez predpisu
 * nie je zaplatená, len nemá čo zaplatiť.
 */
export interface OrderDetail {
  payment_ref: number
  client: Pick<Client, 'id' | 'full_name' | 'email' | 'tel'> | null
  lessons: ClientReservation[]
  fees: ClientFee[]
  payments: Payment[]
  total: string
  paid: string
  due: string
  settled: boolean
  /** Prijaté nad predpis - napr. po zrušení lekcie; na vrátenie. */
  overpaid: string
  /** Koľko z prijatých peňazí sa dá vrátiť (mimo vydaných dokladov). */
  refundable: string
  /** Časť na vydanom potvrdení - pri vrátení peňazí na ňu vznikne storno doklad. */
  refundable_on_receipt: string
  /** Kredit zákazníka objednávky. */
  credit_balance: string
  receipt: OrderReceipt
  /** Doklady k platbám - potvrdenia o platbe a vrátené platby (VP). */
  documents: OrderDocument[]
  /** Faktúry objednávky, aj stornované. */
  invoices?: OrderInvoice[]
}

/** Faktúra klientovi k objednávke (`cashreceipts.invoices`). */
export interface OrderInvoice {
  id: number
  number: string
  total: string
  issue_date: string
  customer_name: string
  storno: boolean
  storno_at: string | null
  /** Číslo dokladu o storne z radu OFA (napr. OFA202600002). */
  storno_number: string | null
  sent_at: string | null
  storno_sent_at: string | null
  created: string
}

export interface InvoiceCustomer {
  customer_name: string
  customer_street: string
  customer_city: string
  customer_postal_code: string
  customer_country: string
  customer_ico: string
  customer_dic: string
  customer_vat_id: string
}

/** `GET /orders/<vs>/invoice` - podklady pre okno „Vystaviť faktúru“. */
export interface InvoiceDraft {
  existing: OrderInvoice | null
  /** Chýbajúce Fakturačné údaje školy (`legal_name`, `ico`…). */
  supplier_missing: string[]
  vat_problem: string | null
  vat_payer: boolean
  vat_rate: string | null
  items: { name: string; date: string | null; time: string; amount: string }[]
  total: string
  base: string
  vat: string
  paid: string
  next_number: string
  issue_date: string
  supply_date: string
  due_days: number
  customer: InvoiceCustomer
}

/** Doklad k jednej platbe (`cashreceipts.documents`). */
export interface OrderDocument {
  id: number
  number: string
  /** `payment` - potvrdenie o platbe, `refund` - vrátená platba (VP). */
  kind: 'payment' | 'refund'
  amount: string
  payment_id: number | null
  created: string
  /** Kedy odišiel klientovi e-mailom; null = ešte neodoslaný. */
  sent_at: string | null
}

/**
 * Podklady na vystavenie potvrdenia o platbe.
 *
 * Doklad hovorí „prijali sme“, takže sa dá vystaviť len na peniaze, ktoré už
 * prišli a ešte nie sú zdokladované — to je `available`.
 */
export interface OrderReceipt {
  /** Modul potvrdení je pre školu zapnutý. Inak je toto jediné pole. */
  enabled: boolean
  can_issue?: boolean
  available?: string
  next_number?: string
  /** Ďalšie lekcie klienta, ktoré sa dajú na doklad pridať. */
  candidates?: Array<{
    id: number
    start: string | null
    end: string | null
    payment_ref: number | null
    amount: string
  }>
  /** Doklady, ktoré túto objednávku už kryjú. */
  issued?: Array<{
    id: number
    number: string
    amount: string
    storno: boolean
    created: string
  }>
}

export interface InstructorReward {
  id: number
  updated: string
  type_id: number | null
  type_name: string | null
  /** Názov vlastného bonusu; pri vstavanom type null (preklad podľa kľúča). */
  type_label?: string | null
  /** Výplatný list, na ktorom odmena je - taká sa nemení. */
  payslip_id?: number | null
  unique_id: string | null
  description: string | null
  instructor_id: number | null
  instructor_name: string | null
  instructor_reservation_id: number | null
  reservation_start: string | null
  client_id: number | null
  client_name: string | null
  client_reservation_id: number | null
  lesson_start: string | null
  number: number
  reward: string
  paid: boolean
}

export interface RewardType {
  id: number
  type: string
  reward: string
  allowed: boolean
  /** Blocks starting before this are never auto-priced. Null = no floor. */
  valid_from: string | null
  /** `builtin` = pevný výpočet, `custom` = vlastný bonus školy (`sql/038`). */
  kind: 'builtin' | 'custom'
  name: string | null
  /** Suma za lekciu, alebo × hodiny lekcie. */
  per: 'lesson' | 'hour'
  position: number
}

/** Aktívny vlastný bonus - checkbox v karte klientskej rezervácie. */
export interface RewardBonus {
  id: number
  name: string
  per: 'lesson' | 'hour'
  /** Len pre toho, kto môže zapisovať. */
  reward?: string
}

export interface FeeType {
  id: number
  type: string
  amount: string
  allowed: boolean
}

export interface PricingRow {
  person: number
  /** Lesson length in minutes. Stored as hours in `pricing.hour`; the API
   *  converts at the boundary so nobody has to type 0.75 for 45 minutes. */
  minutes: number
  lang: string
  price: string
}

export interface RewardsSummary {
  per_type: Array<{ type_id: number; type__type: string; total: string; count: number }>
  total: string
  unpaid: string
  paid: string
  /** Na výplatu - nevyplatené a ešte nie na žiadnom výplatnom liste. */
  to_pay?: string
  to_pay_count?: number
  /** Nevyplatené, už na výplatnom liste. */
  on_payslips?: string
  /** Odučené minúty (len v prehľade všetkých inštruktorov). */
  minutes?: number
}

/** `GET /instructors/<id>/rates` - sadzba jedného druhu odmeny. */
export interface InstructorRateRow {
  type_id: number
  type_name: string
  type_label: string | null
  per: 'lesson' | 'hour' | null
  school_rate: string
  /** Aktuálna sadzba inštruktora; null = platí sadzba školy. */
  rate: string | null
  valid_from: string | null
  /** Sadzba, ktorá sa naozaj platí: vlastná, podľa posledných odmien,
   *  alebo sadzba školy (`effective_source`). */
  effective_rate: string
  effective_source: 'own' | 'rewards' | 'school'
  upcoming: Array<{ id: number; rate: string; valid_from: string }>
  /** `derived` = zmena vyčítaná zo starých odmien (len história, `sql/041`). */
  history: Array<{ id: number; rate: string; valid_from: string; created: string; derived: boolean }>
}

/** Výplatný list inštruktora (`sql/039`). */
export interface Payslip {
  id: number
  number: string
  instructor_id: number
  instructor_name: string
  period_from: string
  period_to: string
  minutes: number
  total: string
  count: number
  status: 'issued' | 'paid' | 'cancelled'
  note: string
  created: string
  paid_at: string | null
  cancelled_at: string | null
}


export interface CashReceipt {
  id: number
  created: string
  prefix: string
  year: number | null
  number: number | null
  document_number: string
  amount: string | null
  receipt_name: string | null
  receipt_description: string | null
  /** Same value set as ClientReservation.client_paid. */
  payment_type: PaymentType
  storno: boolean
  storno_amount: string | null
  /** Stredisko, ktoré doklad vystavilo (`sql/040`). */
  center_id?: number
}

export interface CashReceiptPreview {
  client: { id: number; full_name: string; email: string | null; tel: string | null }
  week_start: string
  week_end: string
  amount: string
  next_number: number
  /** Already formatted, e.g. "PP202600001". */
  next_document_number: string
  unpaid_fees: ClientFee[]
  paid_fees: ClientFee[]
  receipts: CashReceipt[]
}

export interface StatisticsOverview {
  lessons: number
  cancelled: number
  cancelled_share: number
  clients: number
  new_clients: number
  returning_clients: number
  returning_share: number
  instructors: number
  persons: number
  total_minutes: number
  total_hours: number
  available_minutes: number
  /** Odučené oproti dostupnému času z blokov. `null` = žiadne bloky. */
  utilisation: number | null
}

export interface StatisticsMoney {
  revenue: string
  paid: string
  unpaid: string
  discounts: string
  unpaid_lessons: number
  per_lesson: string
  per_client: string
}

export interface StatisticsPeriod {
  period: string
  label: string
  lessons: number
  hours: number
  revenue: string
}

export interface StatisticsInstructor {
  instructor_id: number
  name: string
  lessons: number
  minutes: number
  hours: number
  available_minutes: number
  available_hours: number
  utilisation: number | null
  revenue: string
}

export interface StatisticsBreakdown {
  id: number | null
  label: string
  lessons: number
  share: number
}

/**
 * Jedna sezóna do výberu období. Sezóna je 1. 7. – 30. 6., `value` je
 * `season:2026` a `label` `2026/2027`.
 */
export interface StatisticsSeason {
  value: string
  label: string
  from: string
  to: string
  /** Prebiehajúca sezóna — tá sa ponúka ako prvá a predvypĺňa rozpätie. */
  current: boolean
}

export interface StatisticsResponse {
  interval: string
  from: string
  to: string
  /** `day` | `week` | `month` - krok časovej osi, volí ho server podľa dĺžky. */
  step: string
  overview: StatisticsOverview
  money: StatisticsMoney
  timeline: StatisticsPeriod[]
  /** Rozšírené štatistiky (balíček Profi a vyšší). Bez nich chýba všetko
   *  nižšie okrem `seasons` - aj porovnanie a vlastné obdobie. */
  pro?: boolean
  instructors?: StatisticsInstructor[]
  courses?: StatisticsBreakdown[]
  languages?: StatisticsBreakdown[]
  audience?: { children: number; adults: number; beginners: number }
  duration_of_lessons?: Record<string, number>
  reservations_per_day_of_week?: Record<string, number>
  /** Rovnaké obdobie o rok skôr. Chýba, keď sa porovnanie vypne. */
  previous?: {
    from: string
    to: string
    overview: StatisticsOverview
    money: StatisticsMoney
    timeline: StatisticsPeriod[]
  }
  /** Sezóny, ktoré škola naozaj má — od prvej rezervácie po dnešnú. */
  seasons: StatisticsSeason[]
}

export interface XlogClientReservation {
  id: number
  reservation_id: number | null
  created: string | null
  client_id: number | null
  instructor_reservation_id: number | null
  start: string | null
  end: string | null
  statusid: number | null
  lang_id: number | null
  course_id: number | null
  numbers: number | null
  client_paid: number
  note: string | null
  user_id: number | null
  type: 'insert' | 'update' | 'delete' | null
}

export interface Paginated<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

// --- odchádzajúca pošta -----------------------------------------------------
export type EmailStatus =
  | 'queued'
  | 'sending'
  | 'sent'
  | 'delivered'
  | 'bounced'
  | 'failed'
  | 'cancelled'

/** Riadok fronty. Zoznam vedome nenesie telo správy - to je až v detaile. */
export interface OutgoingEmail {
  id: number
  created: string
  updated: string
  key: string | null
  kind: string
  priority: number
  priority_label: string
  status: EmailStatus
  status_label: string
  scheduled_for: string
  to_email: string
  to_name: string
  subject: string
  attempts: number
  max_attempts: number
  attempts_left: number
  provider: string
  message_id: string
  last_error: string
  sent_at: string | null
  delivered_at: string | null
  failed_at: string | null
  notification_id: number | null
  client_id: number | null
  instructor_id: number | null
  client_reservation_id: number | null
}

export interface EmailEvent {
  id: number
  created: string
  event: string
  detail: string
  occurred_at: string | null
}

export interface OutgoingEmailDetail extends OutgoingEmail {
  body_text: string
  body_html: string
  context: Record<string, unknown>
  events: EmailEvent[]
}

/** Počet správ v jednotlivých stavoch - `{queued: 3, sent: 120}`. */
export type EmailSummary = Partial<Record<EmailStatus, number>>

// --- widget na online rezerváciu --------------------------------------------

export type CapacityMode = 'limit' | 'blocks'
export type AdminNotice = 'each' | 'digest' | 'none'
export type WidgetMode = 'form' | 'chat'

/** Spotreba rozhovorov za mesiac — podklad na faktúru. */
export interface WidgetUsage {
  month: string
  conversations: number
  bookings: number
  tokens: number
  /** V mene školy, vrátane marže aj prepočtu z dolárov. */
  charge: string
  per_booking: string
  /** Mena, v ktorej sú `charge` a `per_booking` — mena školy. */
  currency: string
}

export interface WidgetChoice {
  value: string
  label: string
}

export interface WidgetConfig {
  enabled: boolean
  /** `form` = otázky a tlačidlá, `chat` = zákazník píše vlastnými slovami. */
  mode: WidgetMode
  greeting: string
  /** `#rrggbb`, prázdne = farba systému. */
  accent: string
  capacity_mode: CapacityMode
  horizon_days: number
  lead_hours: number
  daily_limit: number
  slot_limit: number
  /** 0 = podľa `max_clients_per_reservation` v nastaveniach systému. */
  max_persons: number
  /**
   * Stropy rozhovoru — jediná časť systému, ktorá stojí peniaze za každé
   * použitie a je otvorená komukoľvek. `0` v oboch = bez obmedzenia.
   */
  chat_ip_day_limit: number
  chat_month_budget: string
  admin_notice: AdminNotice
  reply_to: string
  /** Domény, na ktorých sa rám smie zobraziť. Prázdne = nikde. */
  origins: string[]
  /** Hotový kód na vloženie do stránky školy. */
  snippet: string
  widget_url: string
  updated: string
  capacity_modes: WidgetChoice[]
  admin_notices: WidgetChoice[]
  modes: WidgetChoice[]
  /** Bez nastaveného modelu sa režim rozhovoru zapnúť nedá. */
  chat_available: boolean
  usage: WidgetUsage
}

/**
 * Zmluva školy s prevádzkovateľom platformy.
 *
 * Nepochádza z databázy školy, ale z registra — škola ju číta, nemení.
 * Návrhy sa sem nedostanú, iba to, čo už bolo odoslané na podpis.
 */
export interface SchoolContract {
  id: number
  number: string
  state: 'sent' | 'signed' | 'terminated' | 'expired'
  /** Preložený názov stavu z backendu — číselník je v Pythone. */
  state_label: string
  period_from: string
  period_to: string
  price: string
  currency: string
  signed_at: string | null
  signer_name: string
  /** Čaká na podpis a odkaz ešte platí. */
  awaiting_signature: boolean
  /** Adresa podpisovej stránky. Prázdne, keď už podpisovať netreba. */
  sign_url: string
  has_pdf: boolean
  signed: boolean
}

export interface SchoolInvoice {
  id: number
  number: string
  state: 'sent' | 'paid'
  state_label: string
  variable_symbol: string
  issued: string
  due: string
  paid_on: string | null
  overdue: boolean
  period_from: string
  period_to: string
  description: string
  total: string
  currency: string
  contract_number: string
  has_pdf: boolean
  /** Dá sa zaplatiť kartou (Stripe prevádzkovateľa). */
  card?: boolean
}

/** Jedno prijatie VOP školou — verzia, čas a kto klikol. */
export interface TermsAcceptance {
  version: number
  effective_from: string
  accepted_at: string
  user_name: string
}

export interface SchoolTerms {
  version: number | null
  effective_from: string | null
  url: string
  /** Účinné znenie ešte nikto v škole nepotvrdil. */
  needs_acceptance: boolean
  accepted: TermsAcceptance[]
}

/** Celé znenie VOP na prečítanie v aplikácii. */
export interface TermsDocument {
  version: number
  effective_from: string
  title: string
  url: string
  /** HTML z backendu — to isté, čo je na verejnej adrese VOP. */
  body: string
  needs_acceptance: boolean
}

export interface SchoolDocuments {
  contracts: SchoolContract[]
  invoices: SchoolInvoice[]
  terms: SchoolTerms
}

export interface TenantBilling {
  legal_name: string
  street: string
  city: string
  postal_code: string
  /** ISO kód - rozhoduje o DPH. `country` je jeho názov (len na čítanie). */
  country_code: string
  country: string
  /** Overenie IČ DPH vo VIES - len IČ DPH iného štátu EÚ. */
  vat_verified: boolean | null
  vat_checked_at: string | null
  vat_registered_name: string
  ico: string
  dic: string
  vat_id: string
  registration: string
  signatory_name: string
  signatory_role: string
  billing_email: string
  billing_phone: string
  iban: string
  /** Čo ešte chýba na vystavenie zmluvy (`Tenant.missing_for_contract`). */
  missing: string[]
}

/** `clients_reservations.attendance` - 1 odučená, 2 klient neprišiel. */
export type Attendance = 1 | 2

export interface InstructorPortalLesson {
  id: number
  time: string
  name: string
  course: string
  lang: string
  numbers: number
  child: boolean
  beginner: boolean
  note: string
  attendance: Attendance | null
  can_mark: boolean
  /** Stredisko - len pri škole s viacerými (`sql/040`). */
  center?: string
}

export interface InstructorPortal {
  instructor: string
  school: string
  logo_url: string
  /** Jazyk školy - keď jazyk telefónu nepoznáme. */
  lang: string
  day: string
  today: string
  lessons: InstructorPortalLesson[]
}

/** `POST /client-reservations/quote/` - cena pred uložením rezervácie. */
/** Variant cenníka, ktorý chýba: osoby, minúty, jazyk (prázdny = cudzí). */
export interface MissingTariff {
  person: number
  minutes: number
  lang: string
  /** Cenník strediska (`sql/040`). */
  center_id?: number
}

export interface ReservationQuote {
  lessons: Array<{ start: string; end: string; price: string | null; tariff: MissingTariff | null }>
  total: string
  /** Termíny, pre ktoré cenník nemá sadzbu. */
  unpriced: number
}

/** `POST /rewards/quote-lessons/` - odmena za lekcie pred uložením. */
export interface RewardLessonQuote {
  rows: Array<{
    type_id: number
    type_name: string
    type_label?: string | null
    number: number
    reward: string
    start: string | null
    instructor: string
  }>
  total: string
  /** Termíny, ktoré by ostali bez inštruktora - odmenu nemajú. */
  without_instructor: number
}

/** `POST /rewards/quote-block/` - odmena za blok po typoch. */
export interface RewardBlockQuote {
  rows: Array<{ type_name: string; type_label?: string | null; number: number; reward: string; count: number }>
  total: string
}

/** `GET /rewards/history/` - jedna zmena sumy odmeny. */
export interface RewardHistoryRow {
  id: number
  created: string
  type_name: string | null
  type_label?: string | null
  /** Odmena za celý deň (auto, odvoz), nie za lekciu. */
  whole_day: boolean
  old_reward: string | null
  new_reward: string | null
  reason: 'created' | 'recalculated' | 'manual' | 'added' | 'deleted'
  user: string
}
